import { useId } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { CONTROL_CLASS, IDENTIFIER_CLASS, TEXTAREA_CLASS, WideField } from '../_shared/components';

import { copy } from './copy';
import type { ContactValues } from './forms.schema';

/**
 * The five channels `[x]` §21.2 enumerates, and no sixth.
 *
 * **The mapped location is two fields, not an embed.** The locality a reader
 * sees and the target an explicit click opens — because `map_render` is
 * `link-out`, and because the frontend standard bars every third-party runtime
 * origin, remote map tiles included. No provider is named anywhere: no map
 * provider is bound in the configuration table at all, so naming one here would
 * be the interface inventing a vendor binding.
 *
 * **The digit boundary runs through the phone field.** The control holds the
 * machine value in ASCII with `dir="ltr"`, because that is what the public page
 * puts inside `tel:`; the public page renders the Arabic-Indic form for its
 * reader beside it. The two never meet in one string, and nothing parses a
 * displayed numeral back. The email and both URLs take `dir="ltr"` for the same
 * reason while their labels stay Arabic.
 *
 * Two columns from the tablet tier — the short identifiers share a line and the
 * address and the map URL span the width, because a wrapped address is a wrapped
 * address whatever the column count.
 */
export function ContactChannelGrid({ form }: { form: UseFormReturn<ContactValues> }) {
  const phoneId = useId();
  const phoneHintId = useId();
  const emailId = useId();
  const addressId = useId();
  const mapLabelId = useId();
  const mapLabelHintId = useId();
  const mapUrlId = useId();
  const mapUrlHintId = useId();

  const { errors } = form.formState;

  function error(message: string | undefined) {
    return message ? <p className="mb-0 mt-2 text-xs text-danger">{message}</p> : null;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="min-w-0">
        <WideField
          id={phoneId}
          label={copy.channels.phone.label}
          hint={copy.channels.phone.hint}
          hintId={phoneHintId}
        >
          <input
            {...form.register('supportPhone')}
            id={phoneId}
            type="tel"
            dir="ltr"
            data-normalize-digits
            aria-describedby={phoneHintId}
            aria-invalid={errors.supportPhone ? true : undefined}
            className={IDENTIFIER_CLASS}
          />
          {error(errors.supportPhone?.message)}
        </WideField>
      </div>

      <div className="min-w-0">
        <WideField id={emailId} label={copy.channels.email.label}>
          <input
            {...form.register('email')}
            id={emailId}
            type="email"
            dir="ltr"
            aria-invalid={errors.email ? true : undefined}
            className={IDENTIFIER_CLASS}
          />
          {error(errors.email?.message)}
        </WideField>
      </div>

      <div className="min-w-0 md:col-span-2">
        <WideField id={addressId} label={copy.channels.address.label}>
          <textarea {...form.register('address')} id={addressId} rows={2} className={TEXTAREA_CLASS} />
        </WideField>
      </div>

      <div className="min-w-0">
        <WideField
          id={mapLabelId}
          label={copy.channels.mapLabel.label}
          hint={copy.channels.mapLabel.hint}
          hintId={mapLabelHintId}
        >
          <input
            {...form.register('mapLabel')}
            id={mapLabelId}
            type="text"
            aria-describedby={mapLabelHintId}
            className={CONTROL_CLASS}
          />
        </WideField>
      </div>

      <div className="min-w-0">
        <WideField
          id={mapUrlId}
          label={copy.channels.mapUrl.label}
          hint={copy.channels.mapUrl.hint}
          hintId={mapUrlHintId}
        >
          <input
            {...form.register('mapUrl')}
            id={mapUrlId}
            type="url"
            dir="ltr"
            aria-describedby={mapUrlHintId}
            aria-invalid={errors.mapUrl ? true : undefined}
            className={IDENTIFIER_CLASS}
          />
          {error(errors.mapUrl?.message)}
        </WideField>
      </div>
    </div>
  );
}
